import { ThemeSwitcher, LanguageSwitcher } from '@repo/settings';
import { useSidebar } from './ui/sidebar';
import { PanelLeft, LogOut, User, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '@repo/auth';
import { useSharedBranches } from '@repo/hooks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from './ui/dropdown-menu';

export interface HeaderProps {
  title: string;
  showDate?: boolean;
}

export function Header({ title }: HeaderProps) {
  const { toggleSidebar } = useSidebar();
  const { t, i18n } = useTranslation();
  const { user, logout, portal } = useAuth();
  const mobileTitleKey =
    title === 'social.dashboard.title' ? 'social.dashboard.mobileTitle' : title;
  const operationRole = user?.roles?.find((role: any) => {
    const roleName = role.role?.toLowerCase();
    return roleName === 'operation-manager' || roleName === 'operationmanager';
  });
  const shouldLoadBranches = portal !== 'student' && !!operationRole;
  const { data: branches = [] } = useSharedBranches(undefined, { enabled: shouldLoadBranches });
  const operationBranchId =
    typeof operationRole?.branch === 'object'
      ? operationRole.branch?._id
      : operationRole?.branch || operationRole?.branchId || operationRole?.branchIds?.[0];
  const operationBranch = branches.find((branch: any) => branch._id === operationBranchId);
  const operationBranchName = operationBranch
    ? i18n.language === 'ar'
      ? operationBranch.nameAr || operationBranch.nameEn
      : operationBranch.nameEn || operationBranch.nameAr
    : undefined;
  const operationHeaderTitle =
    title === 'operation.dashboard.title' && operationBranchName
      ? `${t('roles.operationManager')} | ${operationBranchName}`
      : t(title);
  const desktopTitle = operationHeaderTitle;
  const mobileTitle =
    title === 'operation.dashboard.title' && operationBranchName
      ? operationBranchName
      : t(mobileTitleKey);

  const displayName = user?.name || t('common.user');
  const displayEmail = user?.email || '';
  const initials = displayName
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();

  return (
    <header className="h-16 md:h-20 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 border-b border-border sticky top-0 z-30 px-4 md:px-6 w-full transition-all duration-300">
      <div className="flex h-full items-center justify-between gap-4">
        {/* --- LEFT SECTION: Mobile Toggle & Brand --- */}
        <div className="flex items-center gap-2 md:gap-4">
          <button
            onClick={toggleSidebar}
            className="p-2 -ml-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
            aria-label="Toggle Menu"
          >
            <PanelLeft size={20} />
          </button>

          {/* Brand / Title */}
          <div className="flex flex-col gap-0.5 md:gap-1">
            <h1 className="font-bold text-lg md:text-2xl my-0.5 md:my-1 leading-none tracking-tight transition-all duration-200">
              <span className="md:hidden">{mobileTitle}</span>
              <span className="hidden md:inline">{desktopTitle}</span>
            </h1>
            <p className="text-xs md:text-sm text-muted-foreground transition-all duration-200 hidden sm:block">
              {new Date().toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>

        {/* --- RIGHT SECTION: Actions & User --- */}
        <div className="flex items-center gap-3 ms-auto">
          <div className="flex items-center gap-1 bg-background/50 border border-border/60 p-1.5 rounded-full  backdrop-blur-sm hover:bg-accent/10 transition-colors">
            <ThemeSwitcher variant="toggle" />
            <div className="w-px h-4 bg-border/60 mx-1" aria-hidden="true" />
            <LanguageSwitcher variant="toggle" />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 pl-1.5 pr-2.5 py-1.5 hover:bg-accent/40 rounded-full transition-all outline-none group border border-transparent hover:border-border/30">
                <div className="size-8.5 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center text-primary font-semibold text-xs border border-primary/10 shadow-sm group-hover:shadow-md transition-all">
                  {initials}
                </div>
                <ChevronDown
                  size={14}
                  className="text-muted-foreground/50 group-hover:text-foreground transition-colors"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 p-1">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1 p-1">
                  <p className="text-sm font-medium leading-none">{displayName}</p>
                  <p className="text-xs leading-none text-muted-foreground truncate opacity-80">
                    {displayEmail}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuGroup>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/profile" className="flex items-center w-full h-10">
                    <User size={16} className="mr-2 opacity-70" />
                    {t('navigation.profile')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                className="h-10 text-destructive focus:text-destructive cursor-pointer focus:bg-destructive/10"
                onClick={() => logout()}
              >
                <LogOut size={16} className="mr-2" />
                {t('auth.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
